import type { HTMLAttributes, ReactElement } from 'react';
import { Slot } from '@radix-ui/react-slot';

import { classNames } from '@m1work/shared-utils';

import styles from './Skeleton.module.scss';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
    /**
     * Cover the child element (a `Heading`, a `Button`, an image's wrapper) instead of rendering a block: the
     * placeholder takes the child's size and shape. Not an `<img>` itself: it can't hold the cover. @default false
     */
    asChild?: boolean;
}

/**
 * A loading placeholder. Wrap the real components with placeholder text so the page keeps its layout while it loads,
 * or render a block sized by `className` where there's no real element. Hidden from screen readers and the keyboard;
 * put `aria-busy` on the loading region.
 *
 * @example
 * <Skeleton asChild>
 *     <Heading size="2xl">Vitamin C 1000, 60 tablets</Heading>
 * </Skeleton>
 * <Skeleton className={styles.image} />
 */
export function Skeleton({ asChild = false, className, ...props }: SkeletonProps): ReactElement {
    const Comp = asChild ? Slot : 'div';

    return (
        <Comp
            className={classNames(styles.skeleton, !asChild && styles.block, className)}
            aria-hidden
            inert
            {...props}
        />
    );
}
